import React, {ReactElement, useEffect, useMemo, useState} from 'react';

import {Spinner} from '../../../../resources/Icons';
import Col from '../../../common/Col';
import Row from '../../../common/Row';

type Props = {
  leftValue?: number;
  rightValue?: number;
  leftLabel: string;
  rightLabel: string;
  isLoading: boolean;
};

const HEIGHT = 350;

const EmissionsChart = ({leftValue, rightValue, leftLabel, rightLabel, isLoading}: Props): ReactElement => {
  const [topValue, setTopValue] = useState<number>();
  const [leftHeight, setLeftHeight] = useState<number>(0);
  const [rightHeight, setRightHeight] = useState<number>(0);

  useEffect(() => {
    if (leftValue && rightValue) {
      setTopValue(Math.ceil(Math.max(leftValue, rightValue) / 10) * 10);
    }
  }, [leftValue, rightValue]);

  useEffect(() => {
    if (topValue !== undefined && leftValue !== undefined && rightValue !== undefined && !isLoading) {
      setLeftHeight((leftValue / topValue) * HEIGHT);
      setRightHeight((rightValue / topValue) * HEIGHT);
    }
  }, [topValue, leftValue, rightValue, isLoading]);

  const stopLines = useMemo(() => {
    if (!topValue) return [];

    const stops = [];
    const increment = topValue <= 10 ? 2 : 10;

    for (let i = 0; i <= topValue; i += increment) {
      stops.push(i);
    }
    return stops.map((value, i) => (
      <div
        key={i}
        className={'absolute left-0 w-full h-px bg-gray-100 first:bg-primary-800'}
        style={{bottom: `${(HEIGHT / topValue) * value}px`}}
      >
        <span className={'absolute top-1 -translate-y-1/2 -translate-x-full pr-2'}>{value}</span>
      </div>
    ));
  }, [topValue]);

  return (
    <div className={'relative w-full text-sm'}>
      <span className={'absolute bottom-0 -rotate-90 origin-top-left mb-sm whitespace-nowrap'}>
        tCO²e emitted per hectare over 20 year period
      </span>
      <div className={'relative ml-[70px] mr-lg mobile:mr-0 mt-md '} style={{height: `${HEIGHT}px`}}>
        {stopLines}
        <div
          className={'absolute bottom-0 left-[28%] w-[35%] -translate-x-1/2 bg-primary-500 transition-all'}
          style={{height: `${leftHeight}px`}}
        />
        <div
          className={'absolute bottom-0 left-[74%] w-[35%] -translate-x-1/2 bg-primary-600 transition-all'}
          style={{height: `${rightHeight}px`}}
        />
        {isLoading && (
          <Col
            className={'absolute -top-px -bottom-px left-0 right-0 bg-white bg-opacity-70 items-center justify-center'}
          >
            <Spinner className={'w-10 h-10'} />
          </Col>
        )}
      </div>
      <Row className={'ml-[70px] mr-lg mobile:mr-0 mt-4 pl-[36px] pr-[22px] gap-xs'}>
        <div className={'min-w-0 grow basis-0 text-center whitespace-nowrap overflow-hidden overflow-ellipsis'}>
          {leftLabel}
        </div>
        <div className={'min-w-0 grow basis-0 text-center whitespace-nowrap overflow-hidden overflow-ellipsis'}>
          {rightLabel}
        </div>
      </Row>
    </div>
  );
};

export default EmissionsChart;
